import { supabase } from './supabaseClient';

export interface ProjectCreationAttempt {
  id?: string;
  user_id: string;
  attempt_time?: string;
  was_allowed: boolean;
  blocked_reasons?: {
    insufficientPoints?: {
      required: number;
      current: number;
    };
    linkedinRequired?: boolean;
  };
  user_points_at_time?: number;
  had_linkedin_auth?: boolean;
}

export interface ProjectCreationMetrics {
  totalAttempts: number;
  successfulCreations: number;
  blockedAttempts: {
    byPoints: number;
    byLinkedIn: number;
    byBoth: number;
    total: number;
  };
  averagePointsOfBlockedUsers: number;
  conversionRate: number;
  timeRange: {
    start: string;
    end: string;
  };
}

export interface AnalyticsDateRange {
  start: Date;
  end: Date;
}

export class ProjectCreationAnalytics {
  /**
   * Log a project creation attempt for analytics tracking
   */
  static async logAttempt(
    userId: string,
    allowed: boolean,
    reasons?: ProjectCreationAttempt['blocked_reasons']
  ): Promise<void> {
    try {
      // Get current user points
      const userPoints = await this.getUserPoints(userId);
      
      // Check LinkedIn authentication status
      const hasLinkedIn = await this.checkLinkedInAuth(userId);

      const attemptData: Omit<ProjectCreationAttempt, 'id'> = {
        user_id: userId,
        was_allowed: allowed,
        blocked_reasons: reasons,
        user_points_at_time: userPoints,
        had_linkedin_auth: hasLinkedIn
      };

      const { error } = await supabase
        .from('project_creation_attempts')
        .insert(attemptData);

      if (error) {
        console.error('Error logging project creation attempt:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to log project creation attempt:', error);
      // Don't throw error to avoid breaking the main flow
    }
  }

  /**
   * Get comprehensive metrics for project creation attempts
   */
  static async getMetrics(dateRange?: AnalyticsDateRange): Promise<ProjectCreationMetrics> {
    try {
      const startDate = dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
      const endDate = dateRange?.end || new Date();

      // Get all attempts in date range
      const { data: attempts, error } = await supabase
        .from('project_creation_attempts')
        .select('*')
        .gte('attempt_time', startDate.toISOString())
        .lte('attempt_time', endDate.toISOString())
        .order('attempt_time', { ascending: false });

      if (error) {
        throw error;
      }

      return this.calculateMetrics(attempts || [], startDate, endDate);
    } catch (error) {
      console.error('Error fetching project creation metrics:', error);
      throw error;
    }
  }

  /**
   * Get metrics aggregated by time period (daily, weekly, monthly)
   */
  static async getAggregatedMetrics(
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    dateRange?: AnalyticsDateRange
  ): Promise<Array<ProjectCreationMetrics & { period: string }>> {
    try {
      const { data, error } = await supabase.rpc('get_project_creation_metrics_by_period', {
        period_type: period,
        start_date: dateRange?.start?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        end_date: dateRange?.end?.toISOString() || new Date().toISOString()
      });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching aggregated metrics:', error);
      throw error;
    }
  }

  /**
   * Get top blocking reasons with counts
   */
  static async getBlockingReasons(dateRange?: AnalyticsDateRange): Promise<Array<{
    reason: string;
    count: number;
    percentage: number;
  }>> {
    try {
      const startDate = dateRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const endDate = dateRange?.end || new Date();

      const { data: attempts, error } = await supabase
        .from('project_creation_attempts')
        .select('blocked_reasons')
        .eq('was_allowed', false)
        .gte('attempt_time', startDate.toISOString())
        .lte('attempt_time', endDate.toISOString());

      if (error) {
        throw error;
      }

      const reasonCounts = new Map<string, number>();
      let totalBlocked = 0;

      attempts?.forEach(attempt => {
        if (attempt.blocked_reasons) {
          totalBlocked++;
          const reasons = attempt.blocked_reasons;
          
          if (reasons.insufficientPoints && reasons.linkedinRequired) {
            reasonCounts.set('Both Points and LinkedIn', (reasonCounts.get('Both Points and LinkedIn') || 0) + 1);
          } else if (reasons.insufficientPoints) {
            reasonCounts.set('Insufficient Points', (reasonCounts.get('Insufficient Points') || 0) + 1);
          } else if (reasons.linkedinRequired) {
            reasonCounts.set('LinkedIn Required', (reasonCounts.get('LinkedIn Required') || 0) + 1);
          }
        }
      });

      return Array.from(reasonCounts.entries()).map(([reason, count]) => ({
        reason,
        count,
        percentage: totalBlocked > 0 ? (count / totalBlocked) * 100 : 0
      })).sort((a, b) => b.count - a.count);
    } catch (error) {
      console.error('Error fetching blocking reasons:', error);
      throw error;
    }
  }

  /**
   * Get user conversion funnel data
   */
  static async getConversionFunnel(dateRange?: AnalyticsDateRange): Promise<{
    totalUsers: number;
    usersWhoAttempted: number;
    usersWhoSucceeded: number;
    usersWhoRetried: number;
    averageAttemptsPerUser: number;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_project_creation_funnel', {
        start_date: dateRange?.start?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        end_date: dateRange?.end?.toISOString() || new Date().toISOString()
      });

      if (error) {
        throw error;
      }

      return data || {
        totalUsers: 0,
        usersWhoAttempted: 0,
        usersWhoSucceeded: 0,
        usersWhoRetried: 0,
        averageAttemptsPerUser: 0
      };
    } catch (error) {
      console.error('Error fetching conversion funnel:', error);
      throw error;
    }
  }

  /**
   * Private helper methods
   */
  private static async getUserPoints(userId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('points')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user points:', error);
        return 0;
      }

      return data?.points || 0;
    } catch (error) {
      console.error('Error fetching user points:', error);
      return 0;
    }
  }

  private static async checkLinkedInAuth(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('check_linkedin_auth', {
        user_id: userId
      });

      if (error) {
        console.error('Error checking LinkedIn auth:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Error checking LinkedIn auth:', error);
      return false;
    }
  }

  private static calculateMetrics(
    attempts: ProjectCreationAttempt[],
    startDate: Date,
    endDate: Date
  ): ProjectCreationMetrics {
    const totalAttempts = attempts.length;
    const successfulCreations = attempts.filter(a => a.was_allowed).length;
    const blockedAttempts = attempts.filter(a => !a.was_allowed);

    let byPoints = 0;
    let byLinkedIn = 0;
    let byBoth = 0;
    let totalPointsOfBlocked = 0;
    let blockedUsersWithPoints = 0;

    blockedAttempts.forEach(attempt => {
      if (attempt.blocked_reasons) {
        const hasPointsIssue = !!attempt.blocked_reasons.insufficientPoints;
        const hasLinkedInIssue = !!attempt.blocked_reasons.linkedinRequired;

        if (hasPointsIssue && hasLinkedInIssue) {
          byBoth++;
        } else if (hasPointsIssue) {
          byPoints++;
        } else if (hasLinkedInIssue) {
          byLinkedIn++;
        }

        if (attempt.user_points_at_time !== null && attempt.user_points_at_time !== undefined) {
          totalPointsOfBlocked += attempt.user_points_at_time;
          blockedUsersWithPoints++;
        }
      }
    });

    const averagePointsOfBlockedUsers = blockedUsersWithPoints > 0 
      ? totalPointsOfBlocked / blockedUsersWithPoints 
      : 0;

    const conversionRate = totalAttempts > 0 
      ? (successfulCreations / totalAttempts) * 100 
      : 0;

    return {
      totalAttempts,
      successfulCreations,
      blockedAttempts: {
        byPoints,
        byLinkedIn,
        byBoth,
        total: blockedAttempts.length
      },
      averagePointsOfBlockedUsers,
      conversionRate,
      timeRange: {
        start: startDate.toISOString(),
        end: endDate.toISOString()
      }
    };
  }
}